import { useEffect, useLayoutEffect, useRef, type KeyboardEvent, type PointerEvent, type RefObject } from 'react';
import { clampPosition, writePosition, type PanelPosition } from './guideStorage';

/**
 * Przesuwanie pływającego elementu przewodnika — panelu i paska po zwinięciu
 * (29.10.2026, Bartek: „niech ten popup będzie przesuwalny, bo na razie nie
 * można go przesunąć, jak jest zminimalizowany”). Jedna reguła dla obu stanów:
 * przeciąganie za uchwyt wskaźnikiem (mysz, dotyk, pióro), strzałki na
 * uchwycie, Home — z powrotem w prawy dolny róg. Położenie zawsze w granicach
 * okna (`clampPosition`) i zapamiętane po puszczeniu (`writePosition`).
 * Element wraca w widok po narysowaniu i przy zmianie rozmiaru okna.
 * Wciśnięcie na przycisku w uchwycie („Zwiń”, „Zamknij”, „Rozwiń”) nie
 * zaczyna przeciągania.
 */

export const MOVE_STEP = 24;

/** Domyślny rozmiar, gdy elementu nie da się zmierzyć (zanim się narysuje). */
const FALLBACK_SIZE = { width: 360, height: 200 };

const KEY_DELTA: Record<string, [number, number]> = {
  ArrowLeft: [-MOVE_STEP, 0],
  ArrowRight: [MOVE_STEP, 0],
  ArrowUp: [0, -MOVE_STEP],
  ArrowDown: [0, MOVE_STEP],
};

/** Położenie po naciśnięciu klawisza na uchwycie; `undefined` — klawisz nie przesuwa. */
export function positionAfterKey(key: string, from: PanelPosition): PanelPosition | undefined {
  const d = KEY_DELTA[key];
  return d ? { x: from.x + d[0], y: from.y + d[1] } : undefined;
}

export const currentViewport = () => ({ width: window.innerWidth, height: window.innerHeight });

/** Rozmiar elementu do ograniczenia położenia (bez pomiaru — domyślny rozmiar panelu). */
export function measuredSize(el: HTMLElement | null | undefined): { width: number; height: number } {
  const r = el?.getBoundingClientRect();
  return r ? { width: r.width, height: r.height } : FALLBACK_SIZE;
}

export function useFloatingDrag({
  elementRef,
  pos,
  setPos,
}: {
  elementRef: RefObject<HTMLElement>;
  pos: PanelPosition | null;
  setPos: (p: PanelPosition | null) => void;
}) {
  const drag = useRef<{ dx: number; dy: number; pointerId: number } | null>(null);
  const posRef = useRef(pos);
  posRef.current = pos;

  const place = (p: PanelPosition) => {
    const next = clampPosition(p, measuredSize(elementRef.current), currentViewport());
    posRef.current = next;
    setPos(next);
    return next;
  };

  // Element w granicach okna: po narysowaniu (otwarcie, zwinięcie, rozwinięcie
  // — położenie zapamiętane na większym ekranie wraca w widok) i przy zmianie
  // rozmiaru okna. Zapamiętanego położenia nie nadpisujemy — tylko pokazujemy.
  const keepInView = () => {
    const p = posRef.current;
    if (!p || !elementRef.current) return;
    const next = clampPosition(p, measuredSize(elementRef.current), currentViewport());
    if (next.x !== p.x || next.y !== p.y) {
      posRef.current = next;
      setPos(next);
    }
  };
  const keepInViewRef = useRef(keepInView);
  keepInViewRef.current = keepInView;
  useLayoutEffect(() => keepInViewRef.current(), []);
  useEffect(() => {
    const onResize = () => keepInViewRef.current();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onPointerDown = (e: PointerEvent<HTMLElement>) => {
    // Przyciski w uchwycie działają kliknięciem, nie przeciąganiem.
    if ((e.target as HTMLElement).closest('button')) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const r = elementRef.current?.getBoundingClientRect();
    if (!r) return;
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, pointerId: e.pointerId };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Przeglądarka bez przechwytywania wskaźnika — przeciąganie i tak działa w obrębie uchwytu.
    }
    e.preventDefault();
  };
  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    place({ x: e.clientX - d.dx, y: e.clientY - d.dy });
  };
  const onPointerUp = () => {
    if (!drag.current) return;
    drag.current = null;
    writePosition(posRef.current);
  };
  /** Strzałki przesuwają element, Home wraca do prawego dolnego rogu. */
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Home') {
      e.preventDefault();
      posRef.current = null;
      setPos(null);
      writePosition(null);
      return;
    }
    const r = elementRef.current?.getBoundingClientRect();
    const from = posRef.current ?? (r ? { x: r.left, y: r.top } : null);
    const next = from ? positionAfterKey(e.key, from) : undefined;
    if (!next) return;
    e.preventDefault();
    writePosition(place(next));
  };

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
    onKeyDown,
  };
}
